import React, { useMemo, useState } from "react";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { IconButton, Box, Tooltip, useTheme, TextField, Button, InputAdornment } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import TimelineIcon from "@mui/icons-material/Timeline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import dayjs from "dayjs";

const ReportsTable = ({ reports = [], onAdd, onViewHistoricalReports, onGenerate }) => {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(1000);

  const filteredRows = useMemo(() => {
    if (!search) return reports;
    const s = search.toLowerCase();
    return reports.filter(row =>
      (row.name || "").toLowerCase().includes(s)
    );
  }, [reports, search]);

  const columns = [
    { 
      field: "name", 
      headerName: "Report Name", 
      flex: 1, 
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
          {params.value || "-"}
        </Box>
      ),
    },
    {
      field: "recentDate",
      headerName: "Last Time Generated",
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => {
        const dateToUse = params.row.recentDate || params.row?.date;
        if (!dateToUse) return null;
        const d = dayjs(dateToUse);
        return d.isValid() ? d.valueOf() : null; // Return timestamp for proper sorting
      },
      renderCell: (params) => {
        const dateToUse = params.row.recentDate || params.row?.date;
        if (!dateToUse) return "-";
        const d = dayjs(dateToUse);
        const formattedDate = d.isValid() ? d.format("MMM DD, YYYY hh:mm A") : dateToUse;
        return (
          <Box sx={{ color: theme.palette.text.secondary }}>
            {formattedDate}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", width: "100%" }}>
          <Tooltip title="View Historical Reports">
            <IconButton 
              color="primary" 
              size="small" 
              onClick={() => {
              
                
                // Ensure we're passing a valid ID
                const reportId = params.row.reportId;
                if (!reportId) {
                  console.error("No report ID found in row data");
                  return;
                }
                
                onViewHistoricalReports(reportId);
              }}
            >
              <TimelineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Generate Report">
            <IconButton 
              color="success" 
              size="small" 
              onClick={() => onGenerate(params.row.reportId, params.row.name)}
            >
              <PlayArrowIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      width: 120,
      align: 'center',
      headerAlign: 'center',
    },
  ];

  // Process reports data to add recent date
  const processedReports = useMemo(() => {
   
    const processed = (reports || []).map((report) => {
    
      if (
        Array.isArray(report?.generations) &&
        report?.generations?.length > 0
      ) {
        const latestGeneration = report?.generations.reduce(
          (latest, current) => {
            const currentDate = new Date(current.date);
            const latestDate = new Date(latest.date);
            return currentDate > latestDate ? current : latest;
          }
        );
        const result = {
          ...report,
          recentDate: latestGeneration?.date,
        };
       
        return result;
      } else {
        const result = {
          ...report,
          recentDate: report?.date,
        };
        
        return result;
      }
    });
    

    return processed;
  }, [reports]);
  const rows = (filteredRows || []).map((row) => ({ ...row, id: row.reportId || row.id }));

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search reports..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "text.disabled" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 300,
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
          />
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAdd}
          sx={{
            borderRadius: 1,
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          Add Report
        </Button>
      </Box>
      <DataGridPro
        rows={rows}
        columns={columns}
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        rowsPerPageOptions={[10, 25, 50, 100, 500, 1000, 2000]}
        pagination
        disableSelectionOnClick
        sx={{
          border: "none",
          height:"100%",
          // height: "calc(100vh - 20px)",
          "& .MuiDataGrid-cell": {
            borderBottom: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.primary,
            fontWeight: 500,
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.grey[100],
            borderBottom: `2px solid ${theme.palette.divider}`,
          },
          "& .MuiDataGrid-columnHeader": {
            fontWeight: 600,
            color: theme.palette.text.primary,
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: theme.palette.action.hover,
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: "transparent",
          },
        }}
      />
    </Box>
  );
};

export default ReportsTable; 